import { TrackerDataType } from '../constants/constants';
import { EngagementKeys } from '../constants/IssueAndServiceKeys';
import {
  DropdownTable,
  Engagement,
  EngagementQueryAdder,
  File as TrackerFile,
  FlatEngagement,
  UpdateEngagementWithFileFieldBody,
  UpdateEngagementWithFilesMutationProps,
  UpdateItemWithFileRemovingFile,
  UpdateItemWithFileRemovingURL,
  UpdateItemWithFileUploadingURL,
} from '../types';
import { openSnackbar } from '../../components/AppSnackbar/snackbarSlice';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import dayjs from '../../utils/dayjs';
import { RecentCreations } from '../hooks/useRecentCreations';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  buildEngagementCreatePayload,
  createEngagement,
  deleteEngagement,
  updateEngagementWithFiles,
  uploadEngagementFiles,
  uploadEngagementUrl,
} from '../requests/engagementsRequest';
import { duplicateEngagementLog } from '../../components/TrackerTable/trackerTableRequest';

interface UseEngagementLogHandlerProps {
  recentCreations: RecentCreations;
  onSuccessCallback: () => Promise<void>;
}

interface CreateEngagementMutationFnProps {
  fields: Engagement;
  files: File[];
}

export const useEngagementLogHandler = ({ recentCreations, onSuccessCallback }: UseEngagementLogHandlerProps) => {
  const dispatch = useDispatch();
  const { markCreated, isNewlyAdded } = recentCreations;

  const queryClient = useQueryClient();
  const invalidateDropdownTable = async () => {
    await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
  };
  const toLinkedIds = (linkedItems: any) => {
    if (!linkedItems) {
      return [];
    }
    const items = Array.isArray(linkedItems) ? linkedItems : [linkedItems];
    return items.map((item) => item?._id ?? item?.id ?? item).filter(Boolean);
  };
  const invalidateLinkedServiceDetails = async (linkedItems: any) => {
    await Promise.all(
      toLinkedIds(linkedItems).map((serviceId) =>
        queryClient.invalidateQueries({ queryKey: ['serviceDetail', serviceId] })
      )
    );
  };

  const createEngagementMutation = useMutation({
    mutationFn: async ({ fields, files }: CreateEngagementMutationFnProps) => {
      const requestPayload = buildEngagementCreatePayload(fields);
      const response = await createEngagement(requestPayload);
      if (response.status !== 201) {
        dispatch(openSnackbar({ severity: 'alert', message: response.statusText }));
        throw new Error(response.statusText);
      }

      const id = response.data.id || null;
      if (id) {
        const uploadResp = await uploadEngagementFiles({ id, files });
        if (uploadResp.status !== 201) {
          dispatch(openSnackbar({ severity: 'warning', message: `File upload failed for newly created engagement` }));
        }

        await Promise.all(
          files
            .filter((file) => file.type === 'url')
            .map((file) =>
              uploadEngagementUrl({ id, file }).then((res) => {
                if (res.status !== 201) {
                  dispatch(
                    openSnackbar({ severity: 'warning', message: `Url upload failed for newly created engagement` })
                  );
                }
              })
            )
        );
      }

      return { id };
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        dispatch(openSnackbar({ severity: 'error', message: error.response.data.message }));
      }
    },
    onSuccess: async ({ id }: { id: string | undefined }, variables) => {
      await invalidateDropdownTable();
      if (id && markCreated) {
        markCreated(id);
      }
      if (id) {
        await invalidateLinkedServiceDetails(variables?.fields?.linkedService);
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const updateEngagementWithFileMutation = useMutation({
    mutationFn: async ({
      id,
      fields,
      uploadingFiles,
      uploadingUrls,
      removingFiles,
      removingUrls,
    }: UpdateEngagementWithFilesMutationProps) => {
      const data = await updateEngagementWithFiles({
        id,
        fields,
        uploadingFiles,
        uploadingUrls,
        removingFiles,
        removingUrls,
      });
      return data;
    },
    onError: (err) => {
      console.error(`failed to update engagements with files - ${err}`);
    },
    onSuccess: async (_data, variables) => {
      await invalidateDropdownTable();
      await queryClient.invalidateQueries({ queryKey: ['engagementDetail', variables.id] });
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const duplicateEngagementMutation = useMutation({
    mutationFn: async (id: string) => {
      const data = await duplicateEngagementLog(id);
      return data;
    },
    onSuccess: async ({ id }: { id: string | undefined }) => {
      if (id) {
        markCreated(id);
      }
      await queryClient.invalidateQueries({ queryKey: ['engagementDetail'] });
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
      await invalidateDropdownTable();
    },
  });

  const deleteEngagementMutation = useMutation({
    mutationFn: async (id: string) => {
      const data = await deleteEngagement(id);
      return data;
    },
    onSuccess: async (_data, id) => {
      await invalidateDropdownTable();
      await queryClient.removeQueries({ queryKey: ['engagementDetail', id] });
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  return {
    type: TrackerDataType.ENGAGEMENT,
    newItemModalTitle: 'Add a New Engagement',
    createNewItem: async (fields: Engagement, files: File[], successCallback: (arg0: { postId: string }) => void) => {
      const result = await createEngagementMutation.mutateAsync({ fields, files });
      if (successCallback && result?.id) {
        successCallback({ postId: result.id });
      }
    },
    // use as a React Hook
    useListenOnCreation: (rowId: string) => {
      return { isNewlyAdded: isNewlyAdded(rowId) };
    },
    duplicateItem: async (id: string) => {
      await duplicateEngagementMutation.mutateAsync(id);
    },
    editItem: (val: EngagementQueryAdder[keyof EngagementQueryAdder], index: string) => {
      let newVal;
      if (!val) {
        newVal = null;
      } else {
        switch (index) {
          case EngagementKeys.type:
            {
              let array: Engagement['type'] = [];
              (val as EngagementQueryAdder['type']).forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case EngagementKeys.linkedService:
            newVal = (val as EngagementQueryAdder['linkedService']).filter((item) => item !== null);
            break;
          case EngagementKeys.lead:
            {
              let array: Engagement['lead'] = [];
              (val as EngagementQueryAdder['lead']).forEach((obj) => array.push(obj));
              newVal = array;
            }
            break;
          case EngagementKeys.organization:
            newVal = (val as EngagementQueryAdder['organization'])?._id;
            //TODO cancel community member when organization changed
            break;
          case EngagementKeys.communityMember:
            newVal = (val as EngagementQueryAdder['communityMember'])?._id;
            break;
          case EngagementKeys.topic:
          case EngagementKeys.background:
          case EngagementKeys.notes:
          case EngagementKeys.entryDate:
          case EngagementKeys.dueDate:
          case EngagementKeys.date:
            newVal = val;
            break;
          case EngagementKeys.expert:
            newVal = val;
            break;
          case EngagementKeys.units:
          case EngagementKeys.campaign:
            let arr: Engagement['campaign'] = [];
            (val as EngagementQueryAdder['campaign']).forEach((obj) => arr.push(obj));
            newVal = arr;
            break;
          case EngagementKeys.file:
            newVal = val;
            break;
          case EngagementKeys.dept:
            newVal = (val as EngagementQueryAdder['dept']).map((c) => c.name);
            break;
          default:
            newVal = (val as EngagementQueryAdder['communityMember'])?.name;
            break;
        }
      }
      return newVal;
    },
    updateItemWithFile: async (
      { id, fields }: UpdateEngagementWithFileFieldBody,
      uploadingFiles: File[],
      uploadingUrls: UpdateItemWithFileUploadingURL[],
      removingFiles: UpdateItemWithFileRemovingFile[],
      removingUrls: UpdateItemWithFileRemovingURL[]
    ) => {
      await updateEngagementWithFileMutation.mutateAsync({
        id,
        fields,
        uploadingFiles,
        removingFiles,
        uploadingUrls,
        removingUrls,
      });
    },
    deleteItem: async (id: string) => {
      try {
        const data = await deleteEngagementMutation.mutateAsync(id);
        return data;
      } catch (err) {
        console.error(`failed to delete service - ${err}`);
      }
    },
    flattenForExport: (data: FlatEngagement[], dropdownTable: DropdownTable) => {
      return data.map((row: FlatEngagement) => {
        try {
          type FlatRow = Record<string, any>;
          let flatRow: FlatRow = JSON.parse(JSON.stringify(row.cells));
          // Rich text fields now come from lazy-loaded detail endpoints. Keeping them in CSV export would either
          // require exporting incomplete frontend summary data or reintroduce large payload issues for full-detail fetches.

          delete flatRow[EngagementKeys.date];

          if (flatRow[EngagementKeys.entryDate] && flatRow[EngagementKeys.dueDate]) {
            flatRow['Duration'] = dayjs(flatRow[EngagementKeys.dueDate]).diff(flatRow[EngagementKeys.entryDate], 'day');
          }

          flatRow[EngagementKeys.entryDate] = dayjs(flatRow[EngagementKeys.entryDate]).format('YYYY-MM-DD');
          flatRow[EngagementKeys.dueDate] = flatRow[EngagementKeys.dueDate]
            ? dayjs(flatRow[EngagementKeys.dueDate]).format('YYYY-MM-DD')
            : '';

          flatRow[EngagementKeys.type] = flatRow[EngagementKeys.type]
            .map((c: FlatEngagement['cells'][EngagementKeys.type][0]) => `${c.type}:${c.name}`)
            .join(', \n');

          flatRow[EngagementKeys.expert] = flatRow[EngagementKeys.expert]
            .map(
              (le: FlatEngagement['cells'][EngagementKeys.expert][0]) =>
                dropdownTable['expert'].find((dropdownle) => dropdownle._id === le._id)?.name ?? ''
            )
            .join(', \n');

          flatRow[EngagementKeys.lead] = flatRow[EngagementKeys.lead]
            .map(
              (leadId: FlatEngagement['cells'][EngagementKeys.lead][0]) =>
                dropdownTable['lead'].find((lead) => lead._id === leadId)?.name ?? null
            )
            .filter((leadName: string) => leadName !== null)
            .join(', ');

          flatRow[EngagementKeys.linkedService] = flatRow[EngagementKeys.linkedService].map(
            (c: FlatEngagement['cells'][EngagementKeys.linkedService][0]) => c.name
          );

          const communityMemberEntries = Array.isArray(row.cells[EngagementKeys.communityMember])
            ? row.cells[EngagementKeys.communityMember]
            : [];
          const resolvedCommunityMembers = communityMemberEntries
            .map((id: string) => dropdownTable.communityMember.find((cm) => cm._id === id))
            .filter(Boolean);

          flatRow[EngagementKeys.communityMember] = resolvedCommunityMembers
            .map((cm) => (cm ? cm.name : ''))
            .filter((names) => names !== '')
            .join(', \n');
          flatRow['Community Member Email'] = resolvedCommunityMembers
            .map((cm) => (cm ? cm.email : ''))
            .filter((emails) => emails !== '')
            .join(', \n');
          flatRow['Community Member Phone'] = resolvedCommunityMembers
            .map((cm) => (cm ? cm.phone : ''))
            .filter((phones) => phones !== '')
            .join(', \n');

          const organizationEntries = Array.isArray(row.cells[EngagementKeys.organization])
            ? row.cells[EngagementKeys.organization]
            : [];
          const resolvedOrganizations = organizationEntries
            .map((id: string) => dropdownTable.organization.find((organization) => organization._id === id))
            .filter(Boolean);
          flatRow[EngagementKeys.organization] = resolvedOrganizations
            .map((organization) => (organization ? organization.name : ''))
            .filter((names) => names !== '')
            .join(', \n');

          flatRow[EngagementKeys.file] = flatRow[EngagementKeys.file].map((file: TrackerFile) => {
            if (file.type === 'url') {
              return file.path;
            } else {
              return file.name;
            }
          });
          flatRow[EngagementKeys.campaign] = flatRow[EngagementKeys.campaign]
            .map((campaign: FlatEngagement['cells'][EngagementKeys.campaign][0]) => `${campaign.name}`)
            .join(', \n');

          const contactMethodLabelMap: Record<string, string> = {
            'no intermediary': 'in person',
            phone: 'phone call',
          };
          const contactMethodValue = flatRow[EngagementKeys.contactMethod] as string | undefined;
          const contactMethodKey = typeof contactMethodValue === 'string' ? contactMethodValue : '';
          const contactMethodDisplay =
            contactMethodLabelMap[contactMethodKey] || contactMethodValue || '';
          flatRow = Object.keys(flatRow).reduce<FlatRow>((acc, key) => {
            if (key === EngagementKeys.contactMethod) {
              acc['Mode of Communication'] = contactMethodDisplay;
            } else {
              acc[key] = flatRow[key];
            }
            return acc;
          }, {});

          delete flatRow['Field Addons'];
          delete flatRow[EngagementKeys.notes];
          delete flatRow[EngagementKeys.background];

          return flatRow;
        } catch {
          return {
            [EngagementKeys.entryDate]: '',
            [EngagementKeys.dueDate]: '',
            'Mode of Communication': '',
            [EngagementKeys.topic]: 'error flattening data for export!',
            [EngagementKeys.type]: '',
            [EngagementKeys.units]: '',
            [EngagementKeys.expert]: '',
            [EngagementKeys.organization]: '',
            [EngagementKeys.communityMember]: '',
            'Community Member Email': '',
            'Community Member Phone': '',
            [EngagementKeys.campaign]: '',
            [EngagementKeys.lead]: '',
            [EngagementKeys.file]: '',
            [EngagementKeys.status]: '',
            [EngagementKeys.dept]: '',
            [EngagementKeys.linkedService]: '',
          };
        }
      });
    },
  };
};
